// React
import React, { Component } from "react";

// Libraries
import Numeral from 'numeral';

// Panel
import { PanelHeading, PanelBody, Dropdown } from '../../Components';
import { Holding } from './';

// Styles
import "./dashboard.css";

// Stores 
import { UserProfileStore, SampleDataStore, CurrencyStore } from '../../Stores';

class Holdings extends Component {

/**********************************************************
 *  Constructor
 **********************************************************/
    constructor(props) {

        super(props);

        this.state = {
            
            data : {
                'BTC' : {},
                'USD' : {}
            },

            // dropdowns
            timeframe : 'Today',
            quoteCurrency : 'USD',
            exchange : 'All',

            // sorting
            filterClass : "icon-sort-alpha-asc ",
            sortField : 'name',
            sortAsc: true,

            // arrows
            arrowClass : 'icon-arrow-up8 green-text',

            // panel
            description: ["All of your holdings aggregated across all exchanges, with today's profit and loss numbers.", "Use the menu options to toggle timeframes, such as Today and Since Inception", "Toggle between USD and BTC as the quote currency and other various sorting metrics."],
            loading: true,
            message: null
        };

    }


/**********************************************************
 *  Component Lifecycle Functions
 **********************************************************/

    // After first render, fetch all parameters and the corresponding chart
    componentDidMount() {

        //console.log('Holdings.componentDidMount()');

        // If user logged in, listen for updates
        // and fetch the holdings data
        if(this.props.user !== null) {
            this.listenForUpdates();
            this.getHoldings();
        }
        else {
            this.getSampleHoldings();
        }

    }

/**********************************************************
 *  Helper Functions
 **********************************************************/

    // Retrieves the holdings object from UserProfileStore
    getHoldings() {

        //console.log('Holdings.getHoldings()');

        const holdings = UserProfileStore.getHoldings();
        
        if(holdings !== null) {

            this.setState((prev, props) => {
                return {
                    loading : false,
                    data : holdings,
                    message : null
                }
            });
        }

    }


    // Fetch the sample holdings data and save it
    getSampleHoldings() {
        
        const sampleHoldings = SampleDataStore.getHoldingsData();
        
        this.setState((prev, props) => {
            return {
                loading : false,
                data : sampleHoldings
            }
        });

    }


    // Listen for updates to pnl
    listenForUpdates() {

        var emitter = UserProfileStore.getEmitter();

        // Listen for pnl updates
        emitter.addListener('pnl', (updated) => {

            if(updated) {
                this.getHoldings();
            }
            else {

                const error = UserProfileStore.getPnlError();

                var msg = {};

                // Graceful catch for users that havent added any api keys
                if(Object.keys(error).length > 0 && (error.message.includes("No API Keys available for user") || error.message.includes('No EOD object for user'))) {
                    msg = {
                        title : 'No Api Keys Available', 
                        details : 'Add some Api Keys to see all of your holdings, right here with detailed analytics.',
                        type : ''
                    }
                }
                else {
                    msg = {
                        title : 'Uh Oh..',
                        details : 'Something broke on our end. Try refreshing the panel or contacting us.',
                        type : 'warning'
                    };
                }

                this.setState((prev, props) => {
                    return {
                        loading : false,
                        message : msg
                    }
                });
            }

        });

        // Listen for loading indicator
        emitter.addListener('loadingPnl', (loading) => {

            this.setState((prev, props) => {
                return {
                    loading : true
                }
            });

        });

    }

/**********************************************************
 *  Menu Option Functions
 **********************************************************/

    // Refreshes the pnl
    refreshPnl = () => {
        
        if(this.props.user !== null) {
            UserProfileStore.getPnl(true);
        }

    }


    // Update the quote currency
    selectQuoteCurrency(ticker) {

        this.setState((prev, props) => {
            return {
                quoteCurrency : ticker
            }
        });

    }
    
    // Update the timeframe
    selectTimeframe(timeframe) {

        this.setState((prev, props) => {
            return {
                timeframe : timeframe
            }
        });

    }

    // Update the quote exchange
    selectExchange(exchange) {

        this.setState((prev, props) => {
            return {
                exchange : exchange
            }
        });

    }

    selectFilter(filterClass, sortField, ascending) {
        this.setState((prev, props) => {
            return {
                filterClass : filterClass,
                sortField : sortField,
                sortAsc : ascending
            }
        });
    }


    // Sorts by prop in ascending order
    propComparatorAsc = (prop) => {
        return function(a, b) {
            if(a[prop] > b[prop])
                return 1;
            if(b[prop] > a[prop])
                return -1;

            return 0;
        }
    }

    // Sorts by prop in descending order
    propComparatorDesc = (prop) => {
        return function(a, b) {
            if(a[prop] > b[prop])
                return -1;
            if(b[prop] > a[prop])
                return 1;
            return 0;
        }
    }

    // Given the holdings as an object, return them as a sorted array
    sortHoldings = (holdingsObj) => {

        // convert to array
        var array = [];
        for(let[name, details] of Object.entries(holdingsObj))
            array.push(details);

        var sorted = this.state.sortAsc ? array.sort(this.propComparatorAsc(this.state.sortField)) : array.sort(this.propComparatorDesc(this.state.sortField));

        return sorted;

    }

/**********************************************************
 *  Dynamic UI Building Functions
 **********************************************************/

    // Build the holdings objects
    buildHoldings = () => {

        const data = this.state.data;
        const quoteCurrency = this.state.quoteCurrency;
        const exchange = this.state.exchange;

        const timePnl = this.state.timeframe === 'Today' ? 'dailyPnl' : 'totalPnl';
        const timeChange = this.state.timeframe === 'Today' ? 'dailyChange' : 'totalChange';

        if(Object.keys(data[quoteCurrency]).length === 0)
            return null;
        else {

            const holdings = this.sortHoldings(data[quoteCurrency][exchange]);
            
            return (
                
                holdings.map( (holding) => {

                    return (
                        <Holding key={holding.ticker} name={holding.name} ticker={holding.ticker} quoteCurrency={quoteCurrency} pnl={holding[timePnl]} change={holding[timeChange]} position={holding.position} quantity={holding.quantity} />
                    )

                })
                
            )

        }

    }


/**********************************************************
 *  Render
 **********************************************************/

  render() {

    return (

        <div className="panel panel-flat bg-theme-700">

            <PanelHeading title={"Holdings"} description={this.state.description} sample={this.props.user === null ? true : false} hiddenMenu={true} refresh={ this.props.user === null ? null : this.refreshPnl }>

                <Dropdown icon={this.state.timeframe === 'Today' ? 'icon-calendar52' : 'icon-calendar'} value=  {this.state.timeframe}>
                    <li onClick={() => this.selectTimeframe('Today')}><a href={null}><i className='icon-calendar52'></i>Today</a></li>
                    <li onClick={() => this.selectTimeframe('Since Signup')}><a href={null}><i className='icon-calendar'></i>Since Signup</a></li>
                </Dropdown>  

                <Dropdown icon='icon-coins' value={this.state.quoteCurrency}>
                {
                    Object.keys(this.state.data).sort().map((currency) => {
                        return (
                            <li key={currency} onClick={() => this.selectQuoteCurrency(currency)}><a href={null}><div className='currency-symbol'>{CurrencyStore.getCurrencySymbol(currency)}</div>{currency}</a></li>
                        )
                    })
                }
                </Dropdown>

                <Dropdown icon={this.state.filterClass} value={''}>
                    <li onClick={() => this.selectFilter("icon-sort-alpha-asc", 'name', true)}>
                        <a href={null}><i className="icon-sort-alpha-asc"></i>A to Z</a>
                    </li>

                    <li onClick={() => this.selectFilter("icon-sort-alpha-desc", 'name', false)}>
                        <a href={null}><i className=" icon-sort-alpha-desc"></i>Z to A</a>
                    </li>

                    <li onClick={() => this.selectFilter("icon-sort-numberic-desc", 'position', false)}>
                        <a href={null}><i className="icon-sort-numberic-desc"></i>Position High</a>
                    </li>

                    <li onClick={() => this.selectFilter("icon-sort-numeric-asc", 'position', true)}>
                        <a href={null}><i className="icon-sort-numeric-asc"></i>Position Low</a>
                    </li>

                    <li onClick={() => this.selectFilter("icon-sort-numberic-desc", this.state.timeframe === 'Today' ? 'dailyPnl' : 'totalPnl', false)}>
                        <a href={null}><i className="icon-sort-numberic-desc"></i>PnL High</a>
                    </li>

                    <li onClick={() => this.selectFilter("icon-sort-numeric-asc", this.state.timeframe === 'Today' ? 'dailyPnl' : 'totalPnl', true)}>
                        <a href={null}><i className="icon-sort-numeric-asc"></i>PnL Low</a>
                    </li>
                </Dropdown>

            </PanelHeading>

            <PanelBody loading={this.state.loading} message={this.state.message}>

                <div className="holding-body">

                    <div className="row">

                        { this.buildHoldings() }

                    </div>

                </div>

            </PanelBody>

        </div>

    );
  }
}

export default Holdings;



// WEBPACK FOOTER //
// ./src/Views/Dashboard/Holdings.js